import React, { useState, useMemo } from 'react'
import DatePicker from 'react-datepicker'
import { Input, Modal, ModalHeader, ModalBody } from 'reactstrap'
import moment from 'moment'

import { COLORS } from '@common'
import TimeWheelPicker from '../TimeWheelPicker'
import './PickupDateTimePicker.css'

const PickupDateTimePicker = ({
    value,
    onChange,
    containerClassName,
    minPickupTime = '07:00', // earliest pickup time (format: "HH:MM")
    maxPickupTime = '19:00', // latest pickup time (format: "HH:MM")
}) => {
    const [isDateModalOpen, setIsDateModalOpen] = useState(false)
    const [isTimeModalOpen, setIsTimeModalOpen] = useState(false)
    const [selectedDate, setSelectedDate] = useState(null)
    const [selectedTime, setSelectedTime] = useState(null)

    // Initialize from value
    const getInitialDate = () => {
        if (!value || !value.dateTime) return null
        return value.dateTime instanceof Date ? value.dateTime : new Date(value.dateTime)
    }

    React.useEffect(() => {
        const dateTime = getInitialDate()
        if (dateTime) {
            setSelectedDate(dateTime)
            setSelectedTime(dateTime)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value])

    // Reset selectedDate when date modal opens
    React.useEffect(() => {
        if (isDateModalOpen) {
            const dateTime = getInitialDate()
            if (dateTime) {
                setSelectedDate(dateTime)
            } else {
                setSelectedDate(null)
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isDateModalOpen, value])

    // Reset selectedTime when time modal opens
    React.useEffect(() => {
        if (isTimeModalOpen) {
            const dateTime = getInitialDate()
            if (dateTime) {
                setSelectedTime(dateTime)
            } else {
                setSelectedTime(null)
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isTimeModalOpen, value])

    // Calculate min date (today)
    const minDate = useMemo(() => {
        return new Date()
    }, [])

    // Calculate min time based on selected date and business hours
    const minTime = useMemo(() => {
        if (!selectedDate) return null

        const today = new Date()
        today.setHours(0, 0, 0, 0)
        const selectedDay = new Date(selectedDate)
        selectedDay.setHours(0, 0, 0, 0)

        // Parse business hours
        const [minHour, minMin] = minPickupTime.split(':').map(Number)
        const businessMinTime = new Date()
        businessMinTime.setHours(minHour, minMin, 0, 0)

        // If selected date is today, min time is max of (current time + 30 minutes rounded up to next 10 minutes, business min time)
        if (selectedDay.getTime() === today.getTime()) {
            const currentMinTime = new Date(Date.now() + 30 * 60 * 1000)
            // Round up to next 10-minute interval
            const minutes = currentMinTime.getMinutes()
            const roundedMinutes = Math.ceil(minutes / 10) * 10
            if (roundedMinutes >= 60) {
                currentMinTime.setHours(currentMinTime.getHours() + 1)
                currentMinTime.setMinutes(0)
            } else {
                currentMinTime.setMinutes(roundedMinutes)
            }
            currentMinTime.setSeconds(0)
            currentMinTime.setMilliseconds(0)
            return currentMinTime > businessMinTime ? currentMinTime : businessMinTime
        }

        // For future dates, min time is business opening time
        return businessMinTime
    }, [selectedDate, minPickupTime])

    // Calculate max time based on business hours
    const maxTime = useMemo(() => {
        const [maxHour, maxMin] = maxPickupTime.split(':').map(Number)
        const businessMaxTime = new Date()
        businessMaxTime.setHours(maxHour, maxMin, 0, 0)
        return businessMaxTime
    }, [maxPickupTime])

    // Generate available time slots based on business hours and intervals (10-minute intervals)
    const availableTimes = useMemo(() => {
        const [minHour, minMin] = minPickupTime.split(':').map(Number)
        const [maxHour, maxMin] = maxPickupTime.split(':').map(Number)
        const times = []

        for (let hour = minHour; hour <= maxHour; hour++) {
            let startMin = hour === minHour ? minMin : 0
            let endMin = hour === maxHour ? maxMin : 59

            // Round startMin to next 10-minute interval (00, 10, 20, 30, 40, 50)
            const roundedStartMin = Math.ceil(startMin / 10) * 10

            // Generate times with 10-minute intervals
            for (let min = roundedStartMin; min <= endMin; min += 10) {
                if (min <= endMin) {
                    const time = new Date()
                    time.setHours(hour, min, 0, 0)
                    times.push(time)
                }
            }
        }

        return times
    }, [minPickupTime, maxPickupTime])

    // Check if today has any available time slots
    const isTodaySelectable = useMemo(() => {
        const now = moment()
        const minDateTime = moment(now).add(30, 'minutes')

        // Round to nearest 10-minute interval
        const currentMin = minDateTime.minute()
        const roundedMin = Math.round(currentMin / 10) * 10
        if (roundedMin >= 60) {
            minDateTime.minute(0).add(1, 'hour')
        } else {
            minDateTime.minute(roundedMin)
        }

        // Parse business hours
        const [minHour, minMin] = minPickupTime.split(':').map(Number)
        const businessMinTime = moment().hour(minHour).minute(minMin).second(0)

        const [maxHour, maxMin] = maxPickupTime.split(':').map(Number)
        const businessMaxTime = moment().hour(maxHour).minute(maxMin).second(0)

        // Use the later of current min time and business min time
        let finalDateTime = minDateTime.isAfter(businessMinTime) ? minDateTime : businessMinTime

        // If the final min time is after business max time, today has no available slots
        return !finalDateTime.isAfter(businessMaxTime)
    }, [minPickupTime, maxPickupTime])

    const handleDateConfirm = date => {
        if (!date) return

        // Use moment for accurate date comparison without timezone issues
        const today = moment().startOf('day')
        const selectedDay = moment(date).startOf('day')
        const isToday = selectedDay.isSame(today, 'day')

        // If today has no available time slots, don't allow selection
        if (isToday && !isTodaySelectable) {
            return
        }

        setSelectedDate(date)
        setIsDateModalOpen(false)

        // If switching to today, check if current time is valid or needs adjustment
        if (isToday) {
            const now = moment()
            const minDateTime = moment(now).add(30, 'minutes')

            // Round up to next 10-minute interval
            const currentMin = minDateTime.minute()
            const roundedMin = Math.ceil(currentMin / 10) * 10
            if (roundedMin >= 60) {
                minDateTime.minute(0).add(1, 'hour')
            } else {
                minDateTime.minute(roundedMin)
            }

            // Parse business hours
            const [minHour, minMin] = minPickupTime.split(':').map(Number)
            const businessMinTime = moment().hour(minHour).minute(minMin).second(0)

            const [maxHour, maxMin] = maxPickupTime.split(':').map(Number)
            const businessMaxTime = moment().hour(maxHour).minute(maxMin).second(0)

            // Use the later of current min time and business min time
            let finalDateTime = minDateTime.isAfter(businessMinTime) ? minDateTime : businessMinTime

            // Check if within business hours
            // Note: Don't auto-change date if past business hours, keep the selected date
            if (finalDateTime.isAfter(businessMaxTime)) {
                // If current time is past business hours, just use the earliest time available (businessMinTime)
                finalDateTime = businessMinTime
            }

            // Check if existing time is still valid
            if (value && value.time) {
                const existingDate = value.dateTime ? moment(value.dateTime) : null
                if (existingDate) {
                    existingDate.year(finalDateTime.year()).month(finalDateTime.month()).date(finalDateTime.date())
                    const existingMinutes = existingDate.hour() * 60 + existingDate.minute()
                    const minMinutes = finalDateTime.hour() * 60 + finalDateTime.minute()

                    // If existing time is still valid (within business hours and after minimum)
                    if (existingMinutes >= minMinutes && existingDate.isSameOrBefore(businessMaxTime)) {
                        finalDateTime = existingDate
                    }
                }
            }

            // Format date without timezone issues using moment
            const formattedDate = finalDateTime.format('YYYY-MM-DD')
            const formattedTime = finalDateTime.format('HH:mm')
            onChange({ date: formattedDate, time: formattedTime, dateTime: finalDateTime.toDate() })
            setSelectedTime(finalDateTime.toDate())
            return
        }

        // If switching to a future date, try to keep the existing time or set to opening time
        const [minHour, minMin] = minPickupTime.split(':').map(Number)

        // Use moment to handle date without timezone issues
        const selectedMoment = moment(date).startOf('day')

        if (value && value.time) {
            const combined = moment(date)
                .hour(parseInt(value.time.split(':')[0]))
                .minute(parseInt(value.time.split(':')[1]))
                .second(0)
            const formattedDate = combined.format('YYYY-MM-DD')
            onChange({ date: formattedDate, time: value.time, dateTime: combined.toDate() })
        } else {
            // Set default time to business opening time for new date selection
            const combined = moment(date).hour(minHour).minute(minMin).second(0)
            const formattedDate = combined.format('YYYY-MM-DD')
            const formattedTime = `${String(minHour).padStart(2, '0')}:${String(minMin).padStart(2, '0')}`
            onChange({ date: formattedDate, time: formattedTime, dateTime: combined.toDate() })
        }
    }

    const handleTimeConfirm = time => {
        setSelectedTime(time)
        setIsTimeModalOpen(false)

        // Combine date and time using moment to avoid timezone issues
        const dateToUse = selectedDate || (value?.dateTime ? moment(value.dateTime) : null)
        if (dateToUse && time) {
            const combined = moment(dateToUse).hour(time.getHours()).minute(time.getMinutes()).second(0)

            // Format date and time using moment
            const formattedDate = combined.format('YYYY-MM-DD')
            const formattedTime = combined.format('HH:mm')
            onChange({ date: formattedDate, time: formattedTime, dateTime: combined.toDate() })
        }
    }

    const formatDateDisplay = () => {
        if (!value || !value.date) return ''
        // Use moment to avoid timezone issues
        return moment(value.date, 'YYYY-MM-DD').format('MMM D, YYYY')
    }

    const formatTimeDisplay = () => {
        if (!value || !value.time) return ''
        // Use moment to format time
        const timeMoment = moment(value.time, 'HH:mm')
        return timeMoment.format('h:mm a')
    }

    return (
        <>
            <div className={`pickup-datetime-picker-separate ${containerClassName || ''}`}>
                <div className="date-section">
                    <h4 className="section-title">Pickup Date</h4>
                    <Input
                        type="text"
                        value={formatDateDisplay()}
                        onClick={() => setIsDateModalOpen(true)}
                        readOnly
                        className="datetime-input"
                        placeholder="Select date"
                    />
                </div>
                <div className="time-section">
                    <h4 className="section-title">Pickup Time</h4>
                    <Input
                        type="text"
                        value={formatTimeDisplay()}
                        onClick={() => setIsTimeModalOpen(true)}
                        readOnly
                        className="datetime-input"
                        placeholder="Select time"
                        disabled={!selectedDate && !value?.date}
                    />
                </div>
            </div>

            {/* Date Modal */}
            <Modal isOpen={isDateModalOpen} toggle={() => setIsDateModalOpen(false)} className="pickup-datetime-modal" centered>
                <ModalHeader toggle={() => setIsDateModalOpen(false)}>Select Pickup Date</ModalHeader>
                <ModalBody>
                    <div className="pickup-datetime-picker">
                        <DatePicker
                            key={`pickup-date-${isDateModalOpen}-${selectedDate?.getTime()}`}
                            selected={selectedDate}
                            onChange={setSelectedDate}
                            minDate={minDate}
                            filterDate={date => {
                                // If today has no available time slots, make it unselectable
                                const today = moment().startOf('day')
                                const selectedDay = moment(date).startOf('day')

                                // If this is today and it has no available slots, filter it out
                                if (selectedDay.isSame(today, 'day') && !isTodaySelectable) {
                                    return false
                                }

                                return true
                            }}
                            dateFormat="MMMM d, yyyy"
                            calendarClassName="pickup-calendar"
                            inline
                            locale="en-GB"
                            selectsRange={false}
                            formatWeekDay={dayName => {
                                const shortDay = dayName.substring(0, 3)
                                return shortDay.charAt(0).toUpperCase() + shortDay.slice(1).toLowerCase()
                            }}
                        />
                    </div>
                    <div className="text-center mt-3">
                        <button
                            className="btn btn-primary"
                            onClick={() => handleDateConfirm(selectedDate)}
                            disabled={!selectedDate}
                            style={{ minWidth: '120px' }}
                        >
                            Confirm
                        </button>
                    </div>
                </ModalBody>
            </Modal>

            {/* Time Modal */}
            <Modal
                isOpen={isTimeModalOpen}
                toggle={() => setIsTimeModalOpen(false)}
                className="pickup-datetime-modal pickup-datetime-modal--time"
                centered
            >
                <ModalHeader toggle={() => setIsTimeModalOpen(false)}>Select Pickup Time</ModalHeader>
                <ModalBody>
                    <div
                        className="mb-3 p-3"
                        style={{
                            backgroundColor: COLORS.WARNING.background,
                            borderRadius: '8px',
                            border: `1px solid ${COLORS.WARNING.border}`,
                        }}
                    >
                        <p className="mb-0" style={{ fontSize: '14px', color: COLORS.WARNING.text }}>
                            <strong>Note:</strong> Not all the dishes/vendors are available at your selected pickup time. Only
                            available menus will be shown to you.
                        </p>
                    </div>
                    {selectedDate || (value && value.date) ?
                        <>
                            <div className="pickup-datetime-picker">
                                <TimeWheelPicker
                                    value={value?.time || (selectedTime ? moment(selectedTime).format('HH:mm') : null)}
                                    onChange={timeString => {
                                        // Convert time string to Date object
                                        const baseDate = selectedDate || (value?.dateTime ? moment(value.dateTime) : moment())
                                        const combined = moment(baseDate)
                                            .hour(parseInt(timeString.split(':')[0]))
                                            .minute(parseInt(timeString.split(':')[1]))
                                            .second(0)
                                        setSelectedTime(combined.toDate())
                                    }}
                                    minTime={minTime ? moment(minTime).format('HH:mm') : minPickupTime}
                                    maxTime={maxTime ? moment(maxTime).format('HH:mm') : maxPickupTime}
                                    intervalMinutes={10}
                                    styles={{
                                        height: 50,
                                        fontSize: 18,
                                        parentHeight: 200,
                                        selectedBackgroundColor: '#4eac6d',
                                        selectedTextColor: '#fff',
                                        unselectedTextColor: '#888',
                                    }}
                                />
                            </div>

                            <div className="text-center mt-3">
                                <button
                                    className="btn btn-primary"
                                    onClick={() => handleTimeConfirm(selectedTime)}
                                    disabled={!selectedTime}
                                    style={{ minWidth: '120px' }}
                                >
                                    Confirm
                                </button>
                            </div>
                        </>
                    :   <div className="text-center p-4">
                            <p>Please select a date first</p>
                        </div>
                    }
                </ModalBody>
            </Modal>
        </>
    )
}

export default PickupDateTimePicker
